<template>
  <div class="w-full h-full">
    <!-- 顶部 Banner（和列表页一样） -->
    <div class="relative w-full h-[200px] sm:h-[300px] md:h-[400px]">
      <img
        src="../../assets/img/fuwu_1.webp"
        alt="service-banner"
        class="w-full object-cover h-full"
      />
      <div class="absolute bottom-0 left-0 right-0 text-white text-[32px] sm:text-[48px] md:text-[60px] lg:text-[72px] font-bold container mx-auto flex flex-col items-start justify-center px-4 sm:px-6">
        <div>服务案例</div>
        <div class="text-[20px] sm:text-[28px] md:text-[36px] lg:text-[48px] -mt-[8px] sm:-mt-[12px] md:-mt-[16px] lg:-mt-[20px] mb-4 sm:mb-6 md:mb-8 lg:mb-10">CASES</div>
      </div>
    </div>

    <!-- 案例详情内容区域 -->
    <div class="container mx-auto pb-8 pt-4 sm:pt-6 md:pt-8 lg:pt-[68px] max-w-[1200px] px-4 sm:px-6 text-center">
      <div
        v-if="loading"
        class="text-[#333333] text-[14px] sm:text-[16px] md:text-[18px] py-10"
      >加载中...</div>
      <div
        v-else-if="errorMessage"
        class="text-[#333333] text-[14px] sm:text-[16px] md:text-[18px] py-10"
      >{{ errorMessage }}</div>
      <template v-else-if="detail">
      <!-- 标题 -->
        <h1 class="text-[#333333] text-[22px] sm:text-[28px] md:text-[32px] lg:text-[36px] font-black mb-3 sm:mb-4 leading-tight">
          {{ detail.title }}
      </h1>
        <div class="text-[#333333] text-[12px] sm:text-[16px] md:text-[14px] mb-1 sm:mb-2 font-normal">{{ detail.tags }}</div>
      <!-- 地点信息 -->
        <div class="text-[#333333] text-[14px] sm:text-[16px] md:text-[20px] mb-2 sm:mb-4 flex text-center justify-center">
          <div class="mr-4">{{ detail.address }}</div>
          <div>{{ detail.shopType }}</div>
      </div>
        <div class="text-[#333333] sm:text-[12px] md:text-[14px] leading-relaxed mb-1 sm:mb-2 md:mb-4">{{ detail.desc }}</div>

        <!-- 主图 -->
        <div class="mb-6 sm:mb-10 flex justify-center">
        <img 
            :src="detail.image"
            :alt="detail.title"
            class="w-full object-cover lg:h-[700px] h-auto rounded-[8px]"
        />
      </div>

        <!-- 案例内容（HTML） -->
        <div
          class="case-content text-left"
          v-html="detail.content"
        ></div>
        <!-- 声明 -->
        <div
          v-if="detail.statement"
          class="case-content  scroll-fade-in"
        >
          {{ detail.statement }}
      </div>
      </template>
      <!-- 很好看的分割线 -->
      <div class="w-full h-[1px] bg-[#808080]"></div>
      <!-- 底部导航按钮 -->
      <div class="flex justify-center gap-3 sm:gap-6 mt-4 sm:mt-10 md:mt-8 sm:mb-12">
        <button
          @click="goToPrevious"
          :disabled="!hasPrevious"
          class="nav-btn"
          :class="{ 'disabled': !hasPrevious }"
        >
          上一篇
        </button>
        <button
          @click="goToNext"
          :disabled="!hasNext"
          class="nav-btn"
          :class="{ 'disabled': !hasNext }"
        >
          下一篇
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import api from "@/api";
import { RequestError } from "@/utils/request";

const route = useRoute();
const router = useRouter();

// 从路由参数获取案例ID
const id = computed(() => route.params.id as string);

type CaseDetail = {
  // 兼容：详情接口返回 caseId；列表可能返回 id
  caseId?: string;
  id?: string;
  title: string;
  tags: string;
  address: string;
  shopType: string;
  desc: string;
  image: string;
  content: string;
  dateTime: string | null;
  lastId: string;
  nextId: string;
  statement: string | null;
};

const detail = ref<CaseDetail | null>(null);
const loading = ref(false);
const errorMessage = ref("");

// 兼容：如果接口没给 lastId/nextId，则用列表顺序推导上一篇/下一篇
const fallbackPrevId = ref<string>("");
const fallbackNextId = ref<string>("");

const resolvedPrevId = computed(() => detail.value?.lastId || fallbackPrevId.value || "");
const resolvedNextId = computed(() => detail.value?.nextId || fallbackNextId.value || "");

// 判断是否有上一篇/下一篇
const hasPrevious = computed(() => !!resolvedPrevId.value);
const hasNext = computed(() => !!resolvedNextId.value);

// 跳转到上一篇
const goToPrevious = () => {
  const prev = resolvedPrevId.value;
  if (prev) router.push(`/service/case/${prev}.html`);
};

// 跳转到下一篇
const goToNext = () => {
  const next = resolvedNextId.value;
  if (next) router.push(`/service/case/${next}.html`);
};

async function ensurePrevNextByList(currentId: string) {
  // 接口已经给了就不需要 fallback
  if (detail.value?.lastId || detail.value?.nextId) return;

  try {
    const res = await api.service.getCaseList();
    const list = Array.isArray(res.data) ? res.data : [];
    const ids: string[] = list
      .map((x: any) => String(x?.id || x?.caseId || ""))
      .filter(Boolean);

    const idx = ids.findIndex((x) => x === currentId);
    /**
     * 兜底按列表顺序导航：
     * - 上一篇：列表中前一条（idx - 1）
     * - 下一篇：列表中后一条（idx + 1）
     * 这样在列表页从上往下点进来时，“下一篇”符合直觉地跳到下一条。
     */
    fallbackPrevId.value = idx > 0 ? (ids[idx - 1] ?? "") : "";
    fallbackNextId.value = idx >= 0 && idx < ids.length - 1 ? (ids[idx + 1] ?? "") : "";
  } catch {
    fallbackPrevId.value = "";
    fallbackNextId.value = "";
  }
}

const loadCaseDetail = async (id: string) => {
  loading.value = true;
  errorMessage.value = "";
  fallbackPrevId.value = "";
  fallbackNextId.value = "";
  try {
    const res = await api.service.getCaseDetail(id);
    detail.value = res.data as CaseDetail;
    // 如果接口没有 lastId/nextId，用列表兜底推导
    await ensurePrevNextByList(String(detail.value?.caseId || detail.value?.id || id));

    // SEO 信息已由路由守卫统一管理，不再在组件中设置
    // 路由守卫会从 API 获取 SEO 内容并更新页面标题和 meta 标签
  } catch (error) {
    detail.value = null;
    if (error instanceof RequestError) {
      errorMessage.value = error.message || "加载失败";
    } else {
      errorMessage.value = "加载失败";
    }
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  if (id.value) {
    loadCaseDetail(id.value);
  }
});

watch(
  () => id.value,
  (id) => {
    if (id) loadCaseDetail(id);
  }
);
</script>

<style scoped>
.case-content {
  line-height: 1.8;
}

.nav-btn {
  background-color: #1d3c7d;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 100px;
}

@media (min-width: 640px) {
  .nav-btn {
    padding: 11px 26px;
    font-size: 15px;
    min-width: 110px;
  }
}

@media (min-width: 768px) {
  .nav-btn {
    padding: 12px 32px;
    font-size: 16px;
  min-width: 120px;
  }
}

.nav-btn:hover:not(.disabled) {
  background-color: #2a4f9e;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(29, 60, 125, 0.3);
}

.nav-btn.disabled {
  background-color: #e5e5e5;
  color: #999999;
  cursor: not-allowed;
  opacity: 0.6;
}
</style>

